// Coffeein7 PWA helpers — install prompt, offline detection, update flow.
//
// This module is deliberately UI-agnostic: it exposes lifecycle hooks that
// React components subscribe to. See <InstallPrompt/> and <OfflineBanner/>.

// ---------- Install prompt ----------

let deferredPrompt = null;
const installListeners = new Set();

const notifyInstall = () => installListeners.forEach((cb) => cb(deferredPrompt !== null));

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        // Chrome fires this when the app is eligible for install. We stop the
        // default mini-infobar and store the event to fire later on button tap.
        e.preventDefault();
        deferredPrompt = e;
        notifyInstall();
        try {
            window.dispatchEvent(new CustomEvent("c7:pwa-install-available"));
            // Analytics — non-blocking, non-PII.
            fetch("/api/analytics/pwa", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ event: "install_prompt_available" }),
            }).catch(() => {});
        } catch (_) {}
    });
    window.addEventListener("appinstalled", () => {
        deferredPrompt = null;
        notifyInstall();
        try {
            fetch("/api/analytics/pwa", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ event: "installed" }),
            }).catch(() => {});
        } catch (_) {}
    });
}

export const onInstallAvailability = (cb) => {
    installListeners.add(cb);
    // Fire once synchronously so a late subscriber gets current state.
    cb(deferredPrompt !== null);
    return () => installListeners.delete(cb);
};

export const promptInstall = async () => {
    if (!deferredPrompt) return { outcome: "unavailable" };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    notifyInstall();
    try {
        fetch("/api/analytics/pwa", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ event: `install_${choice.outcome}` }),
        }).catch(() => {});
    } catch (_) {}
    return choice; // { outcome: "accepted" | "dismissed" }
};

// ---------- Platform detection (for iOS install hint) ----------

export const isIOS = () =>
    typeof navigator !== "undefined" &&
    /iphone|ipod|ipad/i.test(navigator.userAgent) &&
    !window.MSStream;

export const isAndroid = () =>
    typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);

export const isStandalone = () => {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        // iOS Safari specific
        window.navigator.standalone === true
    );
};

// ---------- Dismissal storage ----------

const DISMISS_KEY = "c7_pwa_prompt_dismissed_at";
const DISMISS_DURATION_DAYS = 14;

export const wasDismissedRecently = () => {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const then = Number(raw);
        if (!then) return false;
        const days = (Date.now() - then) / (1000 * 60 * 60 * 24);
        return days < DISMISS_DURATION_DAYS;
    } catch (_) {
        return false;
    }
};

export const markDismissed = () => {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
};

// ---------- Service worker update flow ----------
//
// The service worker calls self.skipWaiting() only when it receives a
// SKIP_WAITING message. We wire that up here so a UI component can offer
// "Update available — reload" and only trigger the swap when the user opts in.
//
// This prevents mid-checkout page swaps that would otherwise break Stripe
// redirect state.

const updateListeners = new Set();

const notifyUpdate = () => updateListeners.forEach((cb) => cb());

export const onUpdateAvailable = (cb) => {
    updateListeners.add(cb);
    return () => updateListeners.delete(cb);
};

// Called once on app boot from index.js.
export const registerServiceWorker = () => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") return;

    window.addEventListener("load", () => {
        navigator.serviceWorker
            .register("/service-worker.js")
            .then((reg) => {
                // Fires when a new SW is installed and waiting to activate.
                reg.addEventListener("updatefound", () => {
                    const nw = reg.installing;
                    if (!nw) return;
                    nw.addEventListener("statechange", () => {
                        if (nw.state === "installed" && navigator.serviceWorker.controller) {
                            // A previous SW is controlling → this is an UPDATE
                            notifyUpdate();
                        }
                    });
                });
            })
            .catch(() => {});

        // When the controller changes (i.e. new SW took over), reload once.
        let reloading = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (reloading) return;
            reloading = true;
            window.location.reload();
        });
    });
};

export const applyUpdate = () => {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
    });
};

// ---------- PWA launch analytics ----------

if (typeof window !== "undefined") {
    window.addEventListener("load", () => {
        if (isStandalone()) {
            try {
                fetch("/api/analytics/pwa", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ event: "pwa_launch" }),
                }).catch(() => {});
            } catch (_) {}
        }
    });
}
